/**
 * «Ждут вашего решения» — итоги звонков (CC-24), как карточка DecisionCard веб-CRM
 * (GiperCRM/client/src/components/calls/decisions/DecisionCard.tsx). Своей логики решения здесь нет:
 * решают прежние ручки CRM (routes/callSuggestions.ts):
 *   • «Принять»      — POST calls/:id/suggestion/accept {} — как предложила система;
 *   • «Решить/Изменить» — тот же accept с правкой: этап и следующий шаг;
 *   • «Отказ»        — accept с этапом отказа и причиной из справочника;
 *   • «Оставить как есть» — POST calls/:id/suggestion/keep.
 * Здесь — чистые правила: какой запрос послать и как держать список, пока ответ в пути.
 */
import type { DecisionItem, DecisionStage } from '@/api/calls';

export type DecisionAction =
  | { kind: 'accept' }
  | { kind: 'keep' }
  | { kind: 'decide'; stageId: string | null; nextStepTitle: string | null; nextStepAt: string | null }
  | { kind: 'lost'; stageId: string; reasonId?: string; comment?: string; reasonText?: string };

/** Есть ли готовое предложение системы, которое можно «Принять» одним нажатием. */
export function hasProposal(item: Pick<DecisionItem, 'kind' | 'suggestion'>): boolean {
  return item.kind === 'proposal' && item.suggestion?.state === 'ready';
}

/** Этап отказа воронки сделки; нет — кнопки «Отказ» нет (как в вебе). */
export function lostStageOf(stages: DecisionStage[]): DecisionStage | null {
  return stages.find((s) => s.stageType === 'lost') ?? null;
}

/** Этапы для «Решить»: все, кроме отказа, по порядку. */
export function openStages(stages: DecisionStage[]): DecisionStage[] {
  return [...stages].filter((s) => s.stageType !== 'lost').sort((a, b) => a.position - b.position);
}

/** Действие → адрес и тело запроса CRM. */
export function decisionRequest(callId: string, a: DecisionAction): { path: string; body: Record<string, unknown> } {
  const base = `/api/crm/calls/${encodeURIComponent(callId)}/suggestion`;
  switch (a.kind) {
    case 'accept':
      // followUps не шлём: «не прислано — все предложенные» (как «Принять» в вебе по умолчанию).
      return { path: `${base}/accept`, body: {} };
    case 'keep':
      return { path: `${base}/keep`, body: { note: null } };
    case 'decide':
      return {
        path: `${base}/accept`,
        body: { stageId: a.stageId, nextStepTitle: a.nextStepTitle, nextStepAt: a.nextStepAt },
      };
    case 'lost':
      return {
        path: `${base}/accept`,
        body: {
          stageId: a.stageId, nextStepTitle: null, nextStepAt: null,
          ...(a.reasonId ? { lostReasonId: a.reasonId, ...(a.comment ? { lostComment: a.comment } : {}) } : { lostReason: a.reasonText ?? a.comment ?? '' }),
        },
      };
  }
}

/** Правка «Решить»: этап шлём, только если он другой; без шага — шаг не ставим. */
export function decideAction(item: Pick<DecisionItem, 'deal'>, form: { stageId: string; withStep: boolean; title: string; at: string | null }): DecisionAction {
  return {
    kind: 'decide',
    stageId: form.stageId && form.stageId !== item.deal.stageId ? form.stageId : null,
    nextStepTitle: form.withStep ? form.title.trim() || null : null,
    nextStepAt: form.withStep ? form.at : null,
  };
}

// ── Состояние списка, пока решения в пути ──

export interface DecisionsState {
  /** Решённые (или решаемые) — убраны из списка сразу, до ответа сервера. */
  hidden: string[];
  /** Какой итог сейчас решается. */
  busy: string | null;
  /** Ошибка по итогу — словами сервера (правила этапов, «уже решили»). */
  errors: Record<string, string>;
}

export const initialDecisions: DecisionsState = { hidden: [], busy: null, errors: {} };

export type DecisionsEvent =
  | { type: 'start'; callId: string; hide: boolean }
  | { type: 'done'; callId: string }
  | { type: 'failed'; callId: string; message: string }
  /** Пришёл свежий список: убираем пометки об итогах, которых в нём уже нет. */
  | { type: 'synced'; ids: string[] };

export function decisionsReducer(s: DecisionsState, e: DecisionsEvent): DecisionsState {
  switch (e.type) {
    case 'start': {
      const errors = { ...s.errors };
      delete errors[e.callId];
      return { hidden: e.hide && !s.hidden.includes(e.callId) ? [...s.hidden, e.callId] : s.hidden, busy: e.callId, errors };
    }
    case 'done':
      return { ...s, busy: s.busy === e.callId ? null : s.busy, hidden: s.hidden.includes(e.callId) ? s.hidden : [...s.hidden, e.callId] };
    case 'failed':
      // Откат: итог возвращается на место с объяснением.
      return {
        hidden: s.hidden.filter((id) => id !== e.callId),
        busy: s.busy === e.callId ? null : s.busy,
        errors: { ...s.errors, [e.callId]: e.message },
      };
    case 'synced': {
      const alive = new Set(e.ids);
      const errors: Record<string, string> = {};
      for (const [k, v] of Object.entries(s.errors)) if (alive.has(k)) errors[k] = v;
      return { ...s, hidden: s.hidden.filter((id) => alive.has(id)), errors };
    }
  }
}

/** Что показать: список сервера без решённых. */
export function visibleDecisions<T extends { callId: string }>(items: T[], s: DecisionsState): T[] {
  return items.filter((i) => !s.hidden.includes(i.callId));
}

/** Строка под именем: этап, когда, длительность. */
export function decisionMeta(item: Pick<DecisionItem, 'deal' | 'endedAt' | 'durationSeconds'>, now = new Date()): string {
  const d = new Date(item.endedAt);
  const time = d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  const same = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  const when = same(d, now) ? `сегодня ${time}` : same(d, new Date(now.getTime() - 86_400_000)) ? `вчера ${time}`
    : `${d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })} ${time}`;
  const s = item.durationSeconds ?? 0;
  const dur = s ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` : null;
  return [item.deal.stageName, when, dur].filter(Boolean).join(' · ');
}

export function ageWords(min: number): string {
  if (min < 60) return `ждёт ${min} мин`;
  const h = Math.floor(min / 60);
  if (h < 24) return `ждёт ${h} ч`;
  return `ждёт ${Math.floor(h / 24)} дн`;
}
